import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntil } from 'rxjs';
import {
  AnnouncementCreateRequest,
  NewAnnouncement,
} from '../../models/announcements';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { QuillModule } from 'ngx-quill';
import { ClickOutsideDirective } from '../../../../shared/directives/click-outside/click-outside.directive';
import {
  ConnectionUser,
  SharedConnectionUsers,
} from '../../../../shared/models/shared-connection-users';
import { AuditLogService } from '../../../../shared/services/audit-log/audit-log.service';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { AnnouncementsService } from '../../services/announcements.service';

interface ExtendedConnectionUser extends ConnectionUser {
  name: string;
}

@Component({
  selector: 'app-announcement-create',
  standalone: true,
  imports: [FormsModule, PopupComponent, QuillModule, ClickOutsideDirective],
  templateUrl: './announcement-create.component.html',
  styleUrl: './announcement-create.component.scss',
})
export class AnnouncementCreateComponent extends DestroyBase implements OnInit {
  private announcementsService = inject(AnnouncementsService);
  private auditLogService = inject(AuditLogService);

  @Output() sent = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  quillConfig = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ color: [] }, { background: [] }],
      ['link'],
    ],
  };

  newEmail: NewAnnouncement = {
    to: '',
    subject: '',
    message: '',
  };

  // Connection/User Selection State
  availableConnections: SharedConnectionUsers[] = [];
  availableUsers: ExtendedConnectionUser[] = [];
  selectedConnection: SharedConnectionUsers | null = null;
  connectionCheckboxState: Record<string, boolean> = {};

  selectedPeopleToShare: string[] = [];
  selectedUsersWithNames: { displayName: string; username: string }[] = [];
  selectedThroughConnections = new Set<string>();

  // Pagination State
  isLoadingConnections = false;
  hasMoreConnections = false;
  isLoadingMoreConnections = false;
  connectionCurrentPage = 1;
  connectionPageSize = 3;

  isLoadingConnectionUsers = false;
  hasMoreUsers = false;
  isLoadingMoreUsers = false;
  userCurrentPage = 1;
  userPageSize = 3;

  isUsersDropdownOpen = false;
  searchText = '';
  composeErrors = {
    to: '',
    subject: '',
    type: '',
  };

  emailChecked = false;
  notificationChecked = false;

  ngOnInit(): void {
    this.connectionCurrentPage = 1;
    this.getAllConnections();
  }

  onSendEmail(): void {
    this.composeErrors = { to: '', subject: '', type: '' };
    this.validateRecipients();
    this.validateSubject();
    this.validateType();

    if (
      !this.composeErrors.to &&
      !this.composeErrors.subject &&
      !this.composeErrors.type &&
      this.newEmail.message
    ) {
      const announcementPayload: AnnouncementCreateRequest = {
        recipients: {
          connections: this.getSelectedConnections(),
          users: this.selectedPeopleToShare,
        },
        subject: this.newEmail.subject,
        message: this.newEmail.message,
        email: this.emailChecked,
        notification: this.notificationChecked,
      };

      this.auditLogService.logFeature({
        featureName: 'email',
        action: 'send',
        meta: {
          recipientsCount: String(
            this.getSelectedConnections().length +
              this.selectedPeopleToShare.length,
          ),
        },
        description: 'User sends an email',
      });

      this.announcementsService
        .sendEmail(announcementPayload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.sent.emit();
          },
          error: (error) => {
            console.error('Error sending email:', error);
          },
        });
    }
  }

  validateRecipients(): void {
    const hasRecipients =
      this.newEmail.to?.trim().length > 0 ||
      this.getSelectedConnections().length > 0 ||
      this.selectedPeopleToShare.length > 0;

    if (hasRecipients) {
      this.composeErrors.to = '';
    } else {
      this.composeErrors.to = 'Recipient is required.';
    }
  }

  validateSubject(): void {
    if (this.newEmail.subject?.trim().length > 0) {
      this.composeErrors.subject = '';
    } else {
      this.composeErrors.subject =
        this.notificationChecked && !this.emailChecked
          ? 'Notification is required.'
          : 'Subject is required.';
    }
  }

  validateType(): void {
    if (this.emailChecked || this.notificationChecked) {
      this.composeErrors.type = '';
    } else {
      this.composeErrors.type = 'Please select an Announcement Type.';
    }
  }

  getAllConnections(): void {
    this.isLoadingConnections = true;
    this.connectionCurrentPage = 1;
    this.announcementsService
      .getConnections(
        this.searchText,
        this.connectionCurrentPage,
        this.connectionPageSize,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.availableConnections = data.results;
          this.connectionCheckboxState = {};
          this.availableConnections.forEach((conn) => {
            this.connectionCheckboxState[conn.connection_name] = false;
          });
          this.hasMoreConnections =
            this.availableConnections.length < data.count;
          this.isLoadingConnections = false;
        },
        error: (error) => {
          console.error('Error fetching connections:', error);
          this.isLoadingConnections = false;
        },
      });
  }

  loadMoreConnections(): void {
    if (this.isLoadingMoreConnections) return;
    this.isLoadingMoreConnections = true;
    this.connectionCurrentPage++;

    this.announcementsService
      .getConnections(
        this.searchText,
        this.connectionCurrentPage,
        this.connectionPageSize,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const newConnections = response.results;
          this.availableConnections = [
            ...this.availableConnections,
            ...newConnections,
          ];
          newConnections.forEach((conn) => {
            this.connectionCheckboxState[conn.connection_name] = false;
          });
          this.hasMoreConnections =
            this.availableConnections.length < response.count;
          this.isLoadingMoreConnections = false;
        },
        error: () => {
          this.isLoadingMoreConnections = false;
        },
      });
  }

  onSelectConnection(connection: SharedConnectionUsers): void {
    if (this.selectedConnection?.id === connection.id) {
      this.selectedConnection = null;
      this.availableUsers = [];
      return;
    }

    this.selectedConnection = connection;
    this.isLoadingConnectionUsers = true;
    this.availableUsers = [];
    this.userCurrentPage = 1;
    this.hasMoreUsers = false;

    this.announcementsService
      .getUsersForConnection(
        connection.id,
        '',
        this.userCurrentPage,
        this.userPageSize,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const connectionWithUsers =
            response.results.find((c) => c.id === connection.id) ||
            response.results[0];

          if (connectionWithUsers) {
            this.availableUsers = connectionWithUsers.users.map(
              (u: ConnectionUser) => ({
                ...u,
                name: `${u.first_name} ${u.last_name}`,
              }),
            );

            if (
              this.getSelectedConnections().includes(connection.connection_name)
            ) {
              this.availableUsers.forEach((user) => {
                this.selectedThroughConnections.add(user.username);
              });
            }

            this.hasMoreUsers = this.availableUsers.length < response.count;
          }
          this.isLoadingConnectionUsers = false;
        },
        error: (error) => {
          console.error('Error fetching users for connection:', error);
          this.isLoadingConnectionUsers = false;
        },
      });
  }

  loadMoreUsers(): void {
    if (this.isLoadingMoreUsers || !this.selectedConnection) return;
    this.isLoadingMoreUsers = true;
    this.userCurrentPage++;

    this.announcementsService
      .getUsersForConnection(
        this.selectedConnection.id,
        '',
        this.userCurrentPage,
        this.userPageSize,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const connectionWithUsers =
            response.results.find(
              (c) => c.id === this.selectedConnection?.id,
            ) || response.results[0];

          if (connectionWithUsers) {
            const newUsers = connectionWithUsers.users.map(
              (u: ConnectionUser) => ({
                ...u,
                name: `${u.first_name} ${u.last_name}`,
              }),
            );
            this.availableUsers = [...this.availableUsers, ...newUsers];

            if (
              this.getSelectedConnections().includes(
                this.selectedConnection!.connection_name,
              )
            ) {
              newUsers.forEach((user: ExtendedConnectionUser) => {
                this.selectedThroughConnections.add(user.username);
              });
            }

            this.hasMoreUsers = this.availableUsers.length < response.count;
          }
          this.isLoadingMoreUsers = false;
        },
        error: (error) => {
          console.error('Error loading more users:', error);
          this.isLoadingMoreUsers = false;
        },
      });
  }

  closeDropdown(): void {
    this.isUsersDropdownOpen = false;
    this.selectedConnection = null;
    this.availableUsers = [];
  }

  getSelectedConnections(): string[] {
    return Object.entries(this.connectionCheckboxState)
      .filter(([, isChecked]) => isChecked)
      .map(([connection]) => connection);
  }

  removeConnection(connection: string): void {
    this.connectionCheckboxState[connection] = false;

    const conn = this.availableConnections.find(
      (c) => c.connection_name === connection,
    );
    if (conn && this.selectedConnection?.id === conn.id) {
      this.availableUsers.forEach((user) => {
        this.selectedThroughConnections.delete(user.username);
      });
    }

    this.updateNewEmailTo();
    this.validateRecipients();
  }

  getSelectedUsers(): string[] {
    return this.selectedUsersWithNames
      .filter((user) => !this.selectedThroughConnections.has(user.username))
      .map((user) => user.displayName);
  }

  removeUser(displayName: string): void {
    const user = this.selectedUsersWithNames.find(
      (u) => u.displayName === displayName,
    );
    if (user) {
      this.selectedPeopleToShare = this.selectedPeopleToShare.filter(
        (username) => username !== user.username,
      );
      this.selectedUsersWithNames = this.selectedUsersWithNames.filter(
        (u) => u.displayName !== displayName,
      );
    }
    this.updateNewEmailTo();
    this.validateRecipients();
  }

  updateNewEmailTo(): void {
    this.newEmail.to = this.selectedPeopleToShare.join(',');
  }

  onSearchUser(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchText = input.value.trim();
    this.getAllConnections();
  }

  onUsersSelection(
    event: Event,
    connection?: SharedConnectionUsers,
    isConnection?: boolean,
    userObj?: { username: string; name: string },
  ): void {
    if (isConnection && connection) {
      if (this.connectionCheckboxState[connection.connection_name]) {
        if (this.selectedConnection?.id === connection.id) {
          this.availableUsers.forEach((user) => {
            this.selectedThroughConnections.add(user.username);

            if (this.selectedPeopleToShare.includes(user.username)) {
              this.selectedPeopleToShare = this.selectedPeopleToShare.filter(
                (u) => u !== user.username,
              );
              this.selectedUsersWithNames = this.selectedUsersWithNames.filter(
                (u) => u.username !== user.username,
              );
            }
          });
        }
      } else {
        if (this.selectedConnection?.id === connection.id) {
          this.availableUsers.forEach((user) => {
            this.selectedThroughConnections.delete(user.username);
          });
        }
      }
    } else if (userObj) {
      const username = userObj.username;
      if (this.selectedPeopleToShare.includes(username)) {
        this.selectedPeopleToShare = this.selectedPeopleToShare.filter(
          (u) => u !== username,
        );
        this.selectedUsersWithNames = this.selectedUsersWithNames.filter(
          (u) => u.username !== username,
        );
        this.selectedThroughConnections.delete(username);
      } else {
        this.selectedPeopleToShare.push(username);
        this.selectedUsersWithNames.push({
          displayName: userObj.name,
          username: userObj.username,
        });
      }
    }
    this.updateNewEmailTo();
    this.validateRecipients();
  }

  onCancelComposing(): void {
    this.cancelled.emit();
  }

  onTypeCheckboxChange() {
    if (this.notificationChecked && !this.emailChecked) {
      this.newEmail.message = '';
    }
    this.validateType();
  }
}
